import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, TrendingUp, ShoppingCart, Package, CreditCard, Users,
  ArrowRight, Plus, FileText, Database, Bell, X, RefreshCw,
  ChevronRight, Clock, Activity
} from 'lucide-react';
import { format } from 'date-fns';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  ResponsiveContainer, Tooltip, Legend, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/common/StatCard';
import Skeleton from '../components/common/Skeleton';
import EmptyState from '../components/common/EmptyState';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import { fetchDashboard } from '../redux/slices/reportSlice';
import { fetchTodaySales, fetchSales } from '../redux/slices/saleSlice';
import { getLowStockAlerts } from '../redux/slices/productSlice';
import { getCreditSummary } from '../redux/slices/customerSlice';
import { fetchTodayStatus } from '../redux/slices/shiftSlice';
import ShiftSummary from '../components/dashboard/ShiftSummary';

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', damping: 20, stiffness: 200 } },
};

const quickActions = [
  { label: 'New Sale', icon: ShoppingCart, path: '/pos', color: 'bg-primary-500' },
  { label: 'Add Product', icon: Plus, path: '/inventory', color: 'bg-emerald-500' },
  { label: 'View Reports', icon: FileText, path: '/reports', color: 'bg-amber-500' },
  { label: 'Create Backup', icon: Database, path: '/backup', color: 'bg-purple-500' },
];

function SalesTicker({ sales = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (sales.length === 0) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % sales.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [sales.length]);

  if (sales.length === 0) return null;

  const sale = sales[currentIndex];
  return (
    <motion.div
      key={currentIndex}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="flex items-center gap-3 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg"
    >
      <Activity size={16} className="text-primary-600 dark:text-primary-400 animate-pulse" />
      <span className="text-sm text-primary-700 dark:text-primary-300">
        New sale: Rs. {sale.total?.toLocaleString()} - {sale.items?.length || 0} items
        {sale.customer?.name && ` - ${sale.customer.name}`}
      </span>
      <span className="text-xs text-primary-500 dark:text-primary-400 ml-auto">
        {sale.createdAt ? format(new Date(sale.createdAt), 'HH:mm') : ''}
      </span>
    </motion.div>
  );
}

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { dashboard, loading: reportLoading } = useSelector((state) => state.reports);
  const { todaySales, sales, loading: salesLoading } = useSelector((state) => state.sales);
  const { alerts } = useSelector((state) => state.products);
  const { creditSummary } = useSelector((state) => state.customers);

  const [greetingIndex, setGreetingIndex] = useState(0);
  const greetings = ['Welcome back', 'Good to see you', 'Hello', 'Hi'];

  useEffect(() => {
    dispatch(fetchDashboard());
    dispatch(fetchTodaySales());
    dispatch(fetchSales({ page: 1, limit: 10 }));
    dispatch(getLowStockAlerts());
    dispatch(getCreditSummary());
    dispatch(fetchTodayStatus());
  }, [dispatch]);

  useEffect(() => {
    const timer = setInterval(() => {
      setGreetingIndex((prev) => (prev + 1) % greetings.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const isLoading = reportLoading || salesLoading;

  const today = format(new Date(), 'EEEE, MMMM do, yyyy');

  const kpiCards = [
    {
      key: 'todaySales',
      icon: DollarSign,
      label: "Today's Sales",
      value: dashboard?.todaySales ?? todaySales?.reduce?.((s, t) => s + (t.total || 0), 0) ?? 0,
      trend: dashboard?.salesGrowth ?? null,
      color: 'blue',
    },
    {
      key: 'todayProfit',
      icon: TrendingUp,
      label: "Today's Profit",
      value: dashboard?.todayProfit ?? 0,
      trend: dashboard?.profitGrowth ?? null,
      color: 'green',
    },
    {
      key: 'transactions',
      icon: ShoppingCart,
      label: 'Transactions',
      value: dashboard?.todayTransactions ?? todaySales?.length ?? 0,
      trend: dashboard?.transactionGrowth ?? null,
      color: 'purple',
    },
    {
      key: 'stockValue',
      icon: Package,
      label: 'Stock Value',
      value: dashboard?.totalStockValue ?? 0,
      trend: dashboard?.stockGrowth ?? null,
      color: 'yellow',
    },
    {
      key: 'pendingCredit',
      icon: CreditCard,
      label: 'Pending Credit',
      value: creditSummary?.totalOutstanding ?? dashboard?.pendingCredit ?? 0,
      trend: dashboard?.creditGrowth ?? null,
      color: 'red',
    },
    {
      key: 'activeCustomers',
      icon: Users,
      label: 'Active Customers',
      value: dashboard?.activeCustomers ?? 0,
      trend: dashboard?.customerGrowth ?? null,
      color: 'purple',
    },
  ];

  const renderCharts = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <Skeleton variant="text" width="40%" className="mb-4" />
              <Skeleton variant="chart" />
            </div>
          ))}
        </div>
      );
    }

    const salesTrend = dashboard?.salesTrend || [];
    const paymentData = dashboard?.paymentDistribution || [];
    const topProducts = dashboard?.topProducts || [];
    const revenueProfit = dashboard?.revenueProfit || dashboard?.salesTrend || [];

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Sales Trend (Last 7 Days)</h3>
          {salesTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={salesTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                <Line type="monotone" dataKey="sales" stroke="#6366f1" strokeWidth={2} dot={{ r: 3, fill: '#6366f1' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No sales data" description="Sales data will appear here" />}
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Payment Method Distribution</h3>
          {paymentData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={paymentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50}>
                  {paymentData.map((_, idx) => (
                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No payment data" description="Payment distribution data will appear here" />}
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Top Products</h3>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topProducts} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No product data" description="Top products data will appear here" />}
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Revenue vs Profit</h3>
          {revenueProfit.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueProfit}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="profit" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No revenue data" description="Revenue and profit data will appear here" />}
        </motion.div>
      </div>
    );
  };

  if (isLoading && !dashboard) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton variant="text" width="300px" height={32} />
        <Skeleton variant="text" width="200px" height={20} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="card" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="chart" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 md:p-6 space-y-6"
    >
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <motion.h1
            key={greetingIndex}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl font-bold text-gray-900 dark:text-white"
          >
            {greetings[greetingIndex]}, {user?.name || 'User'}
          </motion.h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            <Clock size={14} className="inline mr-1" />
            {today}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {alerts?.length > 0 && (
            <Badge variant="danger" size="md">
              <Bell size={14} className="mr-1" />
              {alerts.length} alerts
            </Badge>
          )}
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshCw}
            onClick={() => {
              dispatch(fetchDashboard());
              dispatch(fetchTodaySales());
              dispatch(getLowStockAlerts());
              toast.success('Dashboard refreshed');
            }}
          >
            Refresh
          </Button>
        </div>
      </motion.div>

      <AnimatePresence mode="popLayout">
        {todaySales?.length > 0 && (
          <motion.div
            key="ticker"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <SalesTicker sales={todaySales} />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((card) => (
          <StatCard key={card.key} {...card} />
        ))}
      </motion.div>

      <ShiftSummary />

      {renderCharts()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div variants={itemVariants} className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Transactions</h3>
            <Button variant="ghost" size="sm" icon={ArrowRight} onClick={() => navigate('/reports')}>
              View All
            </Button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {sales?.slice(0, 10).map((sale, idx) => (
              <motion.div
                key={sale._id || idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <ShoppingCart size={14} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {sale.customer?.name || 'Walk-in Customer'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {sale.createdAt ? format(new Date(sale.createdAt), 'MMM dd, HH:mm') : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Rs. {sale.total?.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                    {sale.paymentMethod || 'Cash'}
                  </p>
                </div>
              </motion.div>
            ))}
            {(!sales || sales.length === 0) && (
              <EmptyState title="No transactions yet" description="Sales will appear here once made" />
            )}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Bell size={16} className="text-amber-500" />
                Low Stock Alerts
              </h3>
              {alerts?.length > 0 && (
                <Badge variant="danger">{alerts.length}</Badge>
              )}
            </div>
            {alerts?.length > 0 ? (
              <div className="space-y-2">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <motion.div
                    key={alert._id || idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center justify-between p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-red-500" />
                      <span className="text-sm text-gray-900 dark:text-white">{alert.name}</span>
                    </div>
                    <span className="text-xs font-medium text-red-500">{alert.stock || alert.quantity || 0} left</span>
                  </motion.div>
                ))}
                {alerts.length > 5 && (
                  <button
                    onClick={() => navigate('/inventory')}
                    className="w-full text-xs text-primary-600 dark:text-primary-400 font-medium py-2 hover:underline"
                  >
                    View all {alerts.length} alerts
                  </button>
                )}
              </div>
            ) : (
              <EmptyState title="All stocked up" description="No low stock alerts" />
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action) => (
                <motion.button
                  key={action.label}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate(action.path)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl ${action.color} flex items-center justify-center`}>
                    <action.icon size={20} className="text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{action.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
